import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextInput, Button, Group, ActionIcon, Tooltip } from '@mantine/core';
import { IconSearch, IconSparkles } from '@tabler/icons-react';
import { useOptionalAuth } from '@/hooks/useOptionalAuth';

/**
 * Global search bar used in the header and on the home hero.
 *
 *  - Anonymous viewers always land on the basic catalog search at
 *    <c>/search?q=...</c>. The catalog tab filters by name / description /
 *    ministry locally, so it stays snappy without an AI roundtrip.
 *  - Authenticated viewers default to the AI tab (routes the search bar to
 *    <c>/search?q=...&mode=ai</c>) since they have access to that surface.
 *    The catalog tab still works for them.
 *
 * Anyone can open <c>/search</c> directly; the AI tab is sign-in gated on
 * the page itself, not in this component, so deep links keep working.
 */
export function AISearchBar({ compact = false, autoFocus = false }: { compact?: boolean; autoFocus?: boolean }) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useOptionalAuth();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    const mode = isAuthenticated ? '&mode=ai' : '';
    navigate(`/search?q=${encodeURIComponent(query)}${mode}`);
  };

  const placeholder = isAuthenticated
    ? 'Ask anything — e.g. how do I set up sound on Sunday?'
    : 'Search courses, lessons or ministries…';

  const leftIcon = isAuthenticated ? <IconSparkles size={compact ? 14 : 18} /> : <IconSearch size={compact ? 14 : 18} />;

  if (compact) {
    return (
      <form onSubmit={submit} style={{ width: '100%' }}>
        <TextInput
          size="sm"
          radius="xl"
          leftSection={leftIcon}
          rightSection={
            <Tooltip label={isAuthenticated ? 'Ask the AI' : 'Search the catalog'} openDelay={400}>
              <ActionIcon type="submit" variant="subtle" size="sm" aria-label="Search">
                <IconSearch size={14} />
              </ActionIcon>
            </Tooltip>
          }
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          aria-label="Search the Resource Centre"
        />
      </form>
    );
  }

  return (
    <form onSubmit={submit}>
      <Group gap="xs" align="center" wrap="nowrap">
        <TextInput
          size="lg"
          radius="xl"
          leftSection={leftIcon}
          placeholder={placeholder}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 0 }}
          autoFocus={autoFocus}
          aria-label="Search the Resource Centre"
        />
        <Button
          type="submit"
          size="lg"
          radius="xl"
          leftSection={isAuthenticated ? <IconSparkles size={18} /> : <IconSearch size={18} />}
          aria-label={isAuthenticated ? 'Ask the AI' : 'Search'}
        >
          {isAuthenticated ? 'Ask' : 'Search'}
        </Button>
      </Group>
    </form>
  );
}
